import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Camera, Loader2, CropIcon } from 'lucide-react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AvatarUploadProps {
  userId: string;
  onUploadComplete: (url: string) => void;
}

export function AvatarUpload({ userId, onUploadComplete }: AvatarUploadProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
    aspect: 1
  });
  const imgRef = useRef<HTMLImageElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];
      const fileSize = file.size / 1024 / 1024; // Convert to MB

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          variant: 'destructive',
          title: 'Invalid file type',
          description: 'Please upload an image file.',
        });
        return;
      }

      // Validate file size (5MB limit)
      if (fileSize > 5) {
        toast({
          variant: 'destructive',
          title: 'File too large',
          description: 'Image must be less than 5MB.',
        });
        return;
      }

      // Create preview URL and open crop dialog
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedFile(file);
      setCropDialogOpen(true);
    } catch (error) {
      console.error('Error handling file:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to process image file',
      });
    }
  };

  const getCroppedImg = async (
    image: HTMLImageElement,
    crop: Crop
  ): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    });
  };

  const handleSaveCrop = async () => {
    if (!imgRef.current || !selectedFile) return;

    try {
      setUploading(true);

      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imgRef.current, crop);
      const croppedFile = new File([croppedBlob], selectedFile.name, {
        type: 'image/jpeg'
      });

      // Remove any existing avatar
      const { data: existingFiles } = await supabase.storage
        .from('avatars')
        .list(userId);

      if (existingFiles && existingFiles.length > 0) {
        await supabase.storage
          .from('avatars')
          .remove(existingFiles.map(file => `${userId}/${file.name}`));
      }

      // Generate unique filename with timestamp
      const timestamp = Date.now();
      const fileExt = 'jpg';
      const filePath = `${userId}/avatar-${timestamp}.${fileExt}`;

      // Upload cropped image
      const { data, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, croppedFile, {
          upsert: true,
          contentType: 'image/jpeg',
          cacheControl: '0'
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlError) throw urlError;

      // Add cache busting parameter
      const publicUrlWithCache = `${publicUrl}?t=${timestamp}`;

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          avatar_url: publicUrlWithCache,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Update UI
      onUploadComplete(publicUrlWithCache);

      toast({
        title: 'Success',
        description: 'Profile photo updated successfully',
      });

      // Clean up
      setCropDialogOpen(false);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to upload profile photo',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="absolute bottom-0 right-0">
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label="Upload avatar"
          />
          <Button 
            size="icon" 
            variant="secondary" 
            className="h-8 w-8 rounded-full"
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Camera className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <Dialog open={cropDialogOpen} onOpenChange={setCropDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Crop Profile Photo</DialogTitle>
            <DialogDescription>
              Adjust the crop area to select the part of the image you want to use.
            </DialogDescription>
          </DialogHeader>

          {previewUrl && (
            <div className="relative aspect-square max-h-[400px] overflow-hidden rounded-lg border">
              <ReactCrop
                crop={crop}
                onChange={c => setCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  src={previewUrl}
                  alt="Preview"
                  className="max-h-[400px] w-auto mx-auto"
                  style={{ maxWidth: '100%' }}
                />
              </ReactCrop>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCropDialogOpen(false);
                setSelectedFile(null);
                setPreviewUrl(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCrop}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CropIcon className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}